import { useQuery } from '@apollo/client';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { graphql } from '~/apollo/generated/v4';
import { GeologyType } from '~/apollo/generated/v4/graphql';
import { GeologyTypeIconV4 } from '~/components/common/icons/geology-type-icon-v4';
import { ControlledCollapsibleCard } from '~/components/ui/collapse';
import { ReactSelectInput } from '~/components/ui/forms/react-select-input';
import type { OutcropAdditionalFieldsFormValues } from '~/routes/upload/model/outcrop/$outcropId/additional-fields';
import {
  additionalCarbonateFields,
  additionalClasticFields,
  additionalFieldsConfig,
  additionalStructuralFields,
  type KPFields,
  type OCAddtlField,
} from '~/utils/modules/outcrop-v4';
import { readableGqlEnum, ucwords } from '~/utils/text';

export const OUTCROP_ADDTL_FIELD_OPTIONS = graphql(`
  query OutcropAddtlFieldOptions {
    netToGross: outcropEnumerations(type: NET_TO_GROSS) {
      values
    }
    systemsTract: outcropEnumerations(type: SYSTEMS_TRACT) {
      values
    }
    shorelineTractory: outcropEnumerations(type: SHORELINE_TRACTORY) {
      values
    }
    duneShape: outcropEnumerations(type: DUNE_SHAPE) {
      values
    }
    channelMorphology: outcropEnumerations(type: CHANNEL_MORPHOLOGY) {
      values
    }
    riverProfileLocation: outcropEnumerations(type: RIVER_PROFILE_LOCATION) {
      values
    }
    dominantLithology: outcropEnumerations(type: DOMINANT_LITHOLOGY) {
      values
    }
    waterTemperature: outcropEnumerations(type: WATER_TEMPERATURE) {
      values
    }
    diageneticProcess: outcropEnumerations(type: DIAGENETIC_PROCESS) {
      values
    }
    diageneticSetting: outcropEnumerations(type: DIAGENETIC_SETTING) {
      values
    }
    diageneticGeometry: outcropEnumerations(type: DIAGENETIC_GEOMETRY) {
      values
    }
    tectonicSetting: outcropEnumerations(type: TECTONIC_SETTING) {
      values
    }
    synSedimentaryDeformation: outcropEnumerations(
      type: SYN_SEDIMENTARY_DEFORMATION
    ) {
      values
    }
    faultRocksMembranes: outcropEnumerations(type: FAULT_ROCKS_MEMBRANES) {
      values
    }
    interactionNetwork: outcropEnumerations(type: INTERACTION_NETWORK) {
      values
    }
    reactivation1stPhase: outcropEnumerations(type: REACTIVATION) {
      values
    }
    reactivation2ndPhase: outcropEnumerations(type: REACTIVATION) {
      values
    }
    symmetryGeometry: outcropEnumerations(type: SYMMETRY_GEOMETRY) {
      values
    }
    multipleFolds: outcropEnumerations(type: MULTIPLE_FOLDS) {
      values
    }
    secondaryStructures: outcropEnumerations(type: SECONDARY_STRUCTURES) {
      values
    }
    lateralAggregation: outcropEnumerations(type: LATERAL_AGGREGATION) {
      values
    }
  }
`);

function GeologyTypeSection({
  children,
  geologyType,
  enabled,
  visible,
  toggleVisibility,
}: {
  children: ReactNode;
  geologyType: GeologyType;
  enabled: boolean;
  visible: boolean;
  toggleVisibility: () => unknown;
}) {
  if (!enabled) {
    return null;
  }

  return (
    <ControlledCollapsibleCard
      open={visible}
      onToggle={toggleVisibility}
      title={
        <div className="flex gap-2 items-center">
          <GeologyTypeIconV4 geologyType={geologyType} className="w-6 h-6" />
          {readableGqlEnum(geologyType)}
        </div>
      }
    >
      <div className="grid lg:grid-cols-2 gap-4">{children}</div>
    </ControlledCollapsibleCard>
  );
}

export function OutcropAdditionalFields({
  entity,
  geologyTypes,
  keyParameters,
}: {
  entity: 'outcrop' | 'ae';
  geologyTypes: GeologyType[];
  keyParameters: KPFields[];
}) {
  const { control } = useFormContext<OutcropAdditionalFieldsFormValues>();
  const { data, loading } = useQuery(OUTCROP_ADDTL_FIELD_OPTIONS);

  type SectionName =
    | GeologyType.Clastic
    | GeologyType.Carbonate
    | GeologyType.Structural;

  const enabledSections: Record<SectionName, boolean> = {
    [GeologyType.Clastic]: geologyTypes.includes(GeologyType.Clastic),
    [GeologyType.Carbonate]: geologyTypes.includes(GeologyType.Carbonate),
    [GeologyType.Structural]: geologyTypes.includes(GeologyType.Structural),
  };

  const [visibleSections, setVisibleSections] = useState(enabledSections);

  const toggleSection = (sectionName: SectionName) => () =>
    setVisibleSections({
      ...visibleSections,
      [sectionName]: !visibleSections[sectionName],
    });

  const enumOptions = (fieldName: OCAddtlField) => {
    return data?.[fieldName]?.values ?? [];
  };

  const mapField = (fieldName: OCAddtlField) => {
    const fieldConfig = additionalFieldsConfig[fieldName];

    return (
      <Controller
        key={fieldName}
        control={control}
        name={fieldName}
        render={({ field }) => (
          <ReactSelectInput
            {...field}
            label={fieldConfig.label ?? ucwords(fieldName)}
            options={enumOptions(fieldName).map(value => ({
              value,
              label: value,
            }))}
            multiple={fieldConfig.multiple}
            disabled={loading}
          />
        )}
      />
    );
  };

  return (
    <div className="space-y-4">
      <GeologyTypeSection
        enabled={enabledSections[GeologyType.Clastic]}
        geologyType={GeologyType.Clastic}
        visible={visibleSections[GeologyType.Clastic]}
        toggleVisibility={toggleSection(GeologyType.Clastic)}
      >
        {additionalClasticFields(entity).map(mapField)}
      </GeologyTypeSection>

      <GeologyTypeSection
        enabled={enabledSections[GeologyType.Carbonate]}
        geologyType={GeologyType.Carbonate}
        visible={visibleSections[GeologyType.Carbonate]}
        toggleVisibility={toggleSection(GeologyType.Carbonate)}
      >
        {additionalCarbonateFields().map(mapField)}
      </GeologyTypeSection>

      <GeologyTypeSection
        enabled={enabledSections[GeologyType.Structural]}
        geologyType={GeologyType.Structural}
        visible={visibleSections[GeologyType.Structural]}
        toggleVisibility={toggleSection(GeologyType.Structural)}
      >
        {additionalStructuralFields(keyParameters).map(mapField)}
      </GeologyTypeSection>
    </div>
  );
}
